import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { evaluateLeadSpam } from '../utils/leadSpamHeuristics';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// Calendly inline widget script
const loadCalendlyScript = () => {
  const script = document.createElement('script');
  script.src = 'https://assets.calendly.com/assets/external/widget.js';
  script.async = true;
  document.body.appendChild(script);
  
  return () => {
    document.body.removeChild(script);
  };
};

// TextMessage.Us widget script
const loadTextMessageScript = () => {
  const script = document.createElement('script');
  script.src = 'https://app.textmessage.us/smart-widget.js';
  script.async = true;
  document.body.appendChild(script);
  
  return () => {
    document.body.removeChild(script);
  };
};

// Google Analytics (gtag.js) loader
const loadGoogleAnalytics = () => {
  // Prevent duplicate injection
  if (document.getElementById('ga-gtag')) {
    return () => {};
  }

  // External gtag.js
  const script = document.createElement('script');
  script.id = 'ga-gtag';
  script.async = true;
  script.src = 'https://www.googletagmanager.com/gtag/js?id=G-RKBH8WHZLJ';
  document.body.appendChild(script);

  // Inline initialization
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', 'G-RKBH8WHZLJ');

  return () => {
    const el = document.getElementById('ga-gtag');
    if (el) document.body.removeChild(el);
  };
};

const Home = () => {
  // Track if component is mounted
  const isMounted = useRef(true);
  const calendlyRef = useRef(null);
  const [widgetLoadError, setWidgetLoadError] = useState(true); // Set to true by default to show our custom form
  const [customPhoneNumber, setCustomPhoneNumber] = useState('');
  const [customTextStatus, setCustomTextStatus] = useState({
    submitting: false,
    success: false,
    error: null
  });
  
  // Pricing toggle state
  const [isAnnualBilling, setIsAnnualBilling] = useState(false);
  const [isLegacyPricing, setIsLegacyPricing] = useState(false);
  // Parallax and UI/UX enhancements
  const parallaxRef1 = useRef(null);
  const parallaxRef2 = useRef(null);
  const parallaxRef3 = useRef(null);
  const [showFloatingCta, setShowFloatingCta] = useState(false);

  // Check if legacy pricing is still available
  useEffect(() => {
    const checkLegacyPricing = () => {
      const now = new Date();
      // Set deadline to December 31, 2025 at 11:59:59 PM (fixed date)
      const deadline = new Date(2025, 11, 31, 23, 59, 59);
      const isLegacy = now < deadline;
      console.log('Legacy pricing check:', { now, deadline, isLegacy });
      setIsLegacyPricing(isLegacy);
    };
    
    checkLegacyPricing();
    // Check every minute to update status
    const interval = setInterval(checkLegacyPricing, 60000);
    return () => clearInterval(interval);
  }, []);

  // Store billing interval preference when it changes
  useEffect(() => {
    const interval = isLegacyPricing ? 'legacy' : (isAnnualBilling ? 'yearly' : 'monthly');
    localStorage.setItem('billingInterval', interval);
    window.dispatchEvent(new CustomEvent('billingIntervalChanged'));
  }, [isAnnualBilling, isLegacyPricing]);

  // Custom phone number input handler for our replacement widget
  const handleCustomPhoneChange = (e) => {
    // Only allow numbers
    const value = e.target.value.replace(/\D/g, '');
    
    // Format the phone number as (XXX) XXX-XXXX
    if (value.length <= 3) {
      setCustomPhoneNumber(value);
    } else if (value.length <= 6) {
      setCustomPhoneNumber(`(${value.slice(0, 3)}) ${value.slice(3)}`);
    } else {
      setCustomPhoneNumber(`(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`);
    }
  };

  // Custom form submission handler for our replacement widget
  const handleCustomTextSubmit = async (e) => {
    e.preventDefault();
    
    // Extract just the numbers
    const rawNumber = customPhoneNumber.replace(/\D/g, '');
    
    // Validate phone number (simple check for 10 digits)
    if (rawNumber.length !== 10) {
      setCustomTextStatus({
        submitting: false,
        success: false,
        error: 'Please enter a valid 10-digit phone number'
      });
      return;
    }
    
    setCustomTextStatus({
      submitting: true,
      success: false,
      error: null
    });

    // Simulate a successful submission after a short delay
    setTimeout(() => {
      if (isMounted.current) {
        setCustomTextStatus({
          submitting: false,
          success: true,
          error: null
        });
        
        // Reset form after successful submission
        setTimeout(() => {
          if (isMounted.current) {
            setCustomPhoneNumber('');
            setCustomTextStatus({
              submitting: false,
              success: false,
              error: null
            });
          }
        }, 5000);
      }
    }, 1000);
    
    // In a real implementation, you would send this to your backend
    console.log('Would send text to:', rawNumber);
  };

  // Fix the hidden slides tabindex function - moved outside of useEffect
  const fixHiddenSlidesTabIndex = () => {
    if (!isMounted.current) return;
    
    // Handle all slides with aria-hidden="true"
    const hiddenSlides = document.querySelectorAll('.slick-slide[aria-hidden="true"]');
    hiddenSlides.forEach(slide => {
      const focusableElements = slide.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
      focusableElements.forEach(element => {
        element.setAttribute('tabindex', '-1');
        // Also add aria-hidden to the focusable elements for extra measure
        element.setAttribute('aria-hidden', 'true');
      });
    });

    // Ensure visible slides have properly focusable elements
    const visibleSlides = document.querySelectorAll('.slick-slide:not([aria-hidden="true"])');
    visibleSlides.forEach(slide => {
      const focusableElements = slide.querySelectorAll('a, button, input, select, textarea, [tabindex="-1"]');
      focusableElements.forEach(element => {
        element.removeAttribute('tabindex');
        element.removeAttribute('aria-hidden');
      });
    });
  };

  // Function to fix accessibility issues with partner slider
  const fixPartnerSliderAccessibility = () => {
    if (!isMounted.current) return;
    
    // Specifically target the elements mentioned in the error
    const activeSlides = document.querySelectorAll('.slick-slide.slick-active');
    
    // First, ensure active slides don't have aria-hidden="true"
    activeSlides.forEach(slide => {
      slide.removeAttribute('aria-hidden');
      
      // Make focusable elements within active slides accessible
      const focusableElements = slide.querySelectorAll('a, button, input, select, textarea, [tabindex]');
      focusableElements.forEach(element => {
        element.removeAttribute('tabindex');
        element.removeAttribute('aria-hidden');
      });
    });
    
    // Then, ensure non-active slides have proper aria-hidden and no focusable elements
    const hiddenSlides = document.querySelectorAll('.slick-slide:not(.slick-active)');
    hiddenSlides.forEach(slide => {
      slide.setAttribute('aria-hidden', 'true');
      
      const focusableElements = slide.querySelectorAll('a, button, input, select, textarea, [tabindex]');
      focusableElements.forEach(element => {
        element.setAttribute('tabindex', '-1');
        element.setAttribute('aria-hidden', 'true');
      });
    });
    
    // Specifically target the problematic anchor tags
    const problematicLinks = document.querySelectorAll('.slick-slide[aria-hidden="true"] a.transition-transform.hover\\:scale-105.block');
    problematicLinks.forEach(link => {
      link.setAttribute('tabindex', '-1');
      link.setAttribute('aria-hidden', 'true');
    });
  };

  // Add custom CSS for slider dots
  useEffect(() => {
    // Add custom CSS to style the active dot
    const style = document.createElement('style');
    style.textContent = `
      .screenshot-slider .slick-dots li button:before {
        color: white;
        opacity: 1;
        font-size: 12px;
        border: 2px solid #263883;
        border-radius: 50%;
        width: 12px;
        height: 12px;
        content: '';
      }
      
      .screenshot-slider .slick-dots li.slick-active button:before {
        background-color: #263883;
        opacity: 1;
      }
      
      /* Hide focusable elements in hidden slides for screen readers */
      .slick-slide[aria-hidden="true"] a,
      .slick-slide[aria-hidden="true"] button,
      .slick-slide[aria-hidden="true"] input,
      .slick-slide[aria-hidden="true"] select,
      .slick-slide[aria-hidden="true"] textarea,
      .slick-slide[aria-hidden="true"] [tabindex]:not([tabindex="-1"]) {
        visibility: hidden !important;
        pointer-events: none !important;
      }
    `;
    document.head.appendChild(style);
    
    // Cleanup function
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Handle accessibility for carousel slides
  useEffect(() => {
    // Ensure we're addressing the accessibility issues after component mount and slider initialization
    const initialTimer = setTimeout(() => {
      fixPartnerSliderAccessibility();
      fixHiddenSlidesTabIndex();
    }, 500); // Give slick slider time to initialize
    
    // Set up recurring check to catch any state changes
    const intervalTimer = setInterval(fixPartnerSliderAccessibility, 1000);
    
    // Setup for component unmount
    return () => {
      isMounted.current = false;
      clearTimeout(initialTimer);
      clearInterval(intervalTimer);
    };
  }, []);

  // Initialize reveal-on-scroll and parallax listeners
  useEffect(() => {
    const revealTargets = document.querySelectorAll('[data-reveal]');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -10% 0px' });

    revealTargets.forEach((el) => observer.observe(el));

    const handleScroll = () => {
      const y = window.scrollY || window.pageYOffset;
      if (parallaxRef1.current) parallaxRef1.current.style.transform = `translateY(${y * -0.08}px)`;
      if (parallaxRef2.current) parallaxRef2.current.style.transform = `translateY(${y * -0.14}px)`;
      if (parallaxRef3.current) parallaxRef3.current.style.transform = `translateY(${y * -0.05}px)`;
      const featuresEl = document.getElementById('features');
      const threshold = featuresEl ? Math.max(0, featuresEl.getBoundingClientRect().top + window.scrollY - 100) : 1000;
      setShowFloatingCta(y > threshold);

      // Adjust floating CTA to avoid overlapping cookie banner and footer links
      const ctaEl = document.querySelector('.floating-cta');
      if (ctaEl) {
        const consent = localStorage.getItem('cookieConsent');
        const bannerVisible = !consent;
        const distanceToBottom = document.documentElement.scrollHeight - window.scrollY - window.innerHeight;
        let bottomRem = 1; // base bottom in rem
        if (bannerVisible) bottomRem = Math.max(bottomRem, 5.5);
        if (distanceToBottom < 180) bottomRem = Math.max(bottomRem, 6.5);
        ctaEl.style.bottom = `${bottomRem}rem`;
      }

      // Hide custom cursor while scrolling
      const cursorEl = document.querySelector('.custom-cursor');
      if (cursorEl) cursorEl.classList.add('is-hidden');
      clearTimeout((window).__cursorShowTimer);
      (window).__cursorShowTimer = setTimeout(() => {
        if (cursorEl) cursorEl.classList.remove('is-hidden');
      }, 150);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  // Adjust floating CTA if cookie banner is visible
  useEffect(() => {
    const checkCookieBanner = () => {
      const consent = localStorage.getItem('cookieConsent');
      const bannerVisible = !consent; // banner shows when no choice yet
      const ctaEl = document.querySelector('.floating-cta');
      if (ctaEl) {
        ctaEl.style.bottom = bannerVisible ? '5.5rem' : '1rem';
      }
    };
    checkCookieBanner();
    const onAccepted = () => checkCookieBanner();
    const onRejected = () => checkCookieBanner();
    window.addEventListener('cookiesAccepted', onAccepted);
    window.addEventListener('cookiesRejected', onRejected);
    window.addEventListener('storage', checkCookieBanner);
    return () => {
      window.removeEventListener('cookiesAccepted', onAccepted);
      window.removeEventListener('cookiesRejected', onRejected);
      window.removeEventListener('storage', checkCookieBanner);
    };
  }, []);

  // Load third-party scripts only after cookie consent
  useEffect(() => {
    const handleCookieConsent = () => {
      const consent = localStorage.getItem('cookieConsent');
      if (consent === 'accepted') {
        const cleanupCalendly = loadCalendlyScript();
        const cleanupTextMessage = loadTextMessageScript();
        const cleanupGA = loadGoogleAnalytics();
        
        return () => {
          cleanupCalendly();
          cleanupTextMessage();
          cleanupGA();
        };
      }
    };

    // Check if consent was already given
    const consent = localStorage.getItem('cookieConsent');
    if (consent === 'accepted') {
      const cleanupCalendly = loadCalendlyScript();
      const cleanupTextMessage = loadTextMessageScript();
      const cleanupGA = loadGoogleAnalytics();
      
      return () => {
        cleanupCalendly();
        cleanupTextMessage();
        cleanupGA();
      };
    }

    // Listen for cookie consent events
    const handleCookiesAccepted = () => {
      const cleanupCalendly = loadCalendlyScript();
      const cleanupTextMessage = loadTextMessageScript();
      const cleanupGA = loadGoogleAnalytics();
      
      return () => {
        cleanupCalendly();
        cleanupTextMessage();
        cleanupGA();
      };
    };

    window.addEventListener('cookiesAccepted', handleCookiesAccepted);
    
    return () => {
      window.removeEventListener('cookiesAccepted', handleCookiesAccepted);
    };
  }, []);

  // Custom cursor and button ripple
  useEffect(() => {
    const cursor = document.createElement('div');
    cursor.className = 'custom-cursor';
    document.body.appendChild(cursor);

    const move = (e) => {
      cursor.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0)`;
    };
    const addInteract = () => cursor.classList.add('is-interacting');
    const removeInteract = () => cursor.classList.remove('is-interacting');

    window.addEventListener('mousemove', move, { passive: true });
    document.querySelectorAll('a, button, .card').forEach((el) => {
      el.addEventListener('mouseenter', addInteract);
      el.addEventListener('mouseleave', removeInteract);
    });

    const handleRipple = (ev) => {
      const target = ev.currentTarget;
      const rect = target.getBoundingClientRect();
      target.style.setProperty('--ripple-x', `${ev.clientX - rect.left}px`);
      target.style.setProperty('--ripple-y', `${ev.clientY - rect.top}px`);
      target.classList.add('is-rippling');
      setTimeout(() => target.classList.remove('is-rippling'), 600);
    };
    document.querySelectorAll('.btn').forEach((btn) => {
      btn.addEventListener('click', handleRipple);
    });

    return () => {
      window.removeEventListener('mousemove', move);
      document.body.removeChild(cursor);
      document.querySelectorAll('a, button, .card').forEach((el) => {
        el.removeEventListener('mouseenter', addInteract);
        el.removeEventListener('mouseleave', removeInteract);
      });
      document.querySelectorAll('.btn').forEach((btn) => {
        btn.removeEventListener('click', handleRipple);
      });
    };
  }, []);

  const partnerSliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    pauseOnHover: true,
    swipeToSlide: true,
    draggable: true,
    touchThreshold: 10,
    cssEase: 'ease-in-out',
    pauseOnFocus: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        }
      }
    ],
    beforeChange: (current, next) => {
      // Call our comprehensive fix function
      setTimeout(fixPartnerSliderAccessibility, 0);
    },
    afterChange: (current) => {
      // Fix accessibility after the slide change is complete
      fixPartnerSliderAccessibility();
    }
  };

  const screenshotSliderSettings = {
    dots: true,
    infinite: true,
    speed: 800,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    swipeToSlide: true,
    draggable: true,
    touchThreshold: 10,
    cssEase: 'ease-in-out',
    pauseOnFocus: true,
    fade: true,
    cssEase: 'linear',
    arrows: true,
    beforeChange: (current, next) => {
      // Call our comprehensive fix function
      setTimeout(fixPartnerSliderAccessibility, 0);
    },
    afterChange: (current) => {
      // Fix accessibility after the slide change is complete
      fixPartnerSliderAccessibility();
    }
  };

  const scrollToSection = (sectionId) => {
    // Remove the # from the section ID
    const id = sectionId.replace('#', '');
    
    // Find the element by ID
    const element = document.getElementById(id);
    
    // If element exists, scroll to it
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  /** Scroll to Try Golf Marshal Text and focus the phone field (embedded widget input when available). */
  const navigateToTryMarshalTextAndFocusPhone = () => {
    const section = document.getElementById('try-golf-marshal-text');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    const tryFocusWidgetInput = (attempt = 0) => {
      const root = document.getElementById('try-golf-marshal-text');
      if (!root) return;

      const canFocusInput = (el) => {
        if (!el || el.disabled) return false;
        const t = (el.type || '').toLowerCase();
        if (t === 'hidden' || t === 'submit' || t === 'button') return false;
        return true;
      };

      const inputs = root.querySelectorAll(
        'input[type="tel"], input[inputmode="tel"], input[type="text"], input:not([type]), textarea'
      );
      for (const input of inputs) {
        if (canFocusInput(input)) {
          input.focus({ preventScroll: true });
          return;
        }
      }

      const iframe = root.querySelector('iframe');
      if (iframe) {
        try {
          const doc = iframe.contentDocument || iframe.contentWindow?.document;
          const inp = doc?.querySelector('input[type="tel"], input[inputmode="tel"], input[type="text"], input:not([type]), textarea');
          if (inp && canFocusInput(inp)) {
            inp.focus({ preventScroll: true });
            return;
          }
        } catch {
          // Cross-origin iframe; cannot focus inside
        }
      }

      if (attempt < 50) {
        setTimeout(() => tryFocusWidgetInput(attempt + 1), 120);
      }
    };

    setTimeout(() => tryFocusWidgetInput(), 400);
  };

  const [formStatus, setFormStatus] = useState({
    submitting: false,
    success: false,
    error: null
  });

  // Form handling function
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Check honeypot field - if it's filled out, it's likely a bot
    if (e.target.botcheck.value !== '') {
      // Silently reject the submission
      console.log('Bot submission detected');
      // Fake success to confuse bots
      setTimeout(() => {
        window.location.href = 'https://app.getgolfmarshal.com/signupstarter';
      }, 1000);
      return;
    }
    
    setFormStatus({
      submitting: true,
      success: false,
      error: null
    });

    const formData = {
      name: e.target.name.value,
      email: e.target.email.value,
      course: e.target.course.value
    };

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setFormStatus({
          submitting: false,
          success: true,
          error: null
        });
        // Redirect to the signup page after successful form submission
        window.location.href = 'https://app.getgolfmarshal.com/signupstarter';
      } else {
        setFormStatus({
          submitting: false,
          success: false,
          error: data.message || 'There was an error submitting the form.'
        });
      }
    } catch (error) {
      setFormStatus({
        submitting: false,
        success: false,
        error: 'Network error. Please try again.'
      });
    }
  };

  // Phone number state for demo text component
  const [phoneNumber, setPhoneNumber] = useState('');
  const [textStatus, setTextStatus] = useState({
    submitting: false,
    success: false,
    error: null
  });

  // Lead form (next to Calendly)
  const [leadForm, setLeadForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    org: '',
    phone: ''
  });
  const [leadStatus, setLeadStatus] = useState({
    submitting: false,
    success: false,
    error: null
  });
  const [leadModalOpen, setLeadModalOpen] = useState(false);
  const [ngcoaModalOpen, setNgcoaModalOpen] = useState(false);
  const [ngcoaForm, setNgcoaForm] = useState({
    firstName: '',
    lastName: '',
    courseName: '',
    email: ''
  });
  const [ngcoaStatus, setNgcoaStatus] = useState({
    submitting: false,
    success: false,
    error: null
  });
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const emailRef = useRef(null);
  const orgRef = useRef(null);
  const phoneRef = useRef(null);
  const submitRef = useRef(null);
  const handleLeadChange = (e) => {
    const { name, value } = e.target;
    setLeadForm((prev) => ({ ...prev, [name]: value }));
  };
  const handleLeadPhoneChange = (e) => {
    const prevRaw = leadForm.phone.replace(/\D/g, '');
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    let formatted = digits;
    if (digits.length > 6) {
      formatted = `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
    } else if (digits.length > 3) {
      formatted = `(${digits.slice(0,3)}) ${digits.slice(3)}`;
    }
    setLeadForm((prev) => ({ ...prev, phone: formatted }));
    if (digits.length === 10 && prevRaw.length < 10) {
      // Auto-advance focus to submit when phone is complete
      setTimeout(() => submitRef.current && submitRef.current.focus(), 0);
    }
  };
  const handleLeadKeyDown = (e, field) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (field === 'firstName') {
        lastNameRef.current && lastNameRef.current.focus();
      } else if (field === 'lastName') {
        emailRef.current && emailRef.current.focus();
      } else if (field === 'email') {
        orgRef.current && orgRef.current.focus();
      } else if (field === 'org') {
        phoneRef.current && phoneRef.current.focus();
      } else if (field === 'phone') {
        submitRef.current && submitRef.current.click();
      }
    }
  };
  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    const botcheck = e.target.botcheck?.value ?? '';
    if (String(botcheck).trim()) {
      setLeadStatus({ submitting: false, success: true, error: null });
      setLeadModalOpen(true);
      return;
    }
    if (!leadForm.firstName || !leadForm.lastName || !leadForm.email || !leadForm.org || !leadForm.phone) {
      setLeadStatus({ submitting: false, success: false, error: 'Please fill in all fields' });
      return;
    }
    const emailOk = /.+@.+\..+/.test(leadForm.email);
    if (!emailOk) {
      setLeadStatus({ submitting: false, success: false, error: 'Please enter a valid email' });
      return;
    }
    const rawPhone = leadForm.phone.replace(/\D/g, '');
    if (rawPhone.length !== 10) {
      setLeadStatus({ submitting: false, success: false, error: 'Please enter a valid 10-digit phone' });
      return;
    }
    const spam = evaluateLeadSpam({
      firstName: leadForm.firstName,
      lastName: leadForm.lastName,
      email: leadForm.email,
      org: leadForm.org
    });
    if (spam.blocked) {
      setLeadStatus({
        submitting: false,
        success: false,
        error:
          'We could not submit your request. If you believe this is an error, please email support@getgolfmarshal.com.'
      });
      return;
    }
    setLeadStatus({ submitting: true, success: false, error: null });
    try {
      const apiBase = process.env.REACT_APP_MG2_API_URL || 'https://app.getgolfmarshal.com/api';
      const res = await fetch(`${apiBase}/leads/public`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: leadForm.firstName,
          lastName: leadForm.lastName,
          email: leadForm.email,
          org: leadForm.org,
          phone: rawPhone,
          source: 'mg2land-home',
          botcheck: ''
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || 'Request failed');
      }
      setLeadStatus({ submitting: false, success: true, error: null });
      setLeadModalOpen(true);
      setLeadForm({ firstName: '', lastName: '', email: '', org: '', phone: '' });
    } catch (err) {
      setLeadStatus({
        submitting: false,
        success: false,
        error: err.message || 'Submission failed. Please try again.'
      });
    }
  };

  const handleNgcoaChange = (e) => {
    const { name, value } = e.target;
    setNgcoaForm((prev) => ({ ...prev, [name]: value }));
  };

  const openNgcoaModal = () => {
    setNgcoaForm({ firstName: '', lastName: '', courseName: '', email: '' });
    setNgcoaStatus({ submitting: false, success: false, error: null });
    setNgcoaModalOpen(true);
  };

  const closeNgcoaModal = () => {
    setNgcoaModalOpen(false);
    setNgcoaStatus({ submitting: false, success: false, error: null });
    setNgcoaForm({ firstName: '', lastName: '', courseName: '', email: '' });
  };

  const handleNgcoaSubmit = async (e) => {
    e.preventDefault();
    const botcheck = e.target.botcheck?.value ?? '';
    if (String(botcheck).trim()) {
      setNgcoaStatus({ submitting: false, success: true, error: null });
      return;
    }
    const firstName = ngcoaForm.firstName.trim();
    const lastName = ngcoaForm.lastName.trim();
    const courseName = ngcoaForm.courseName.trim();
    const email = ngcoaForm.email.trim();
    if (!firstName || !lastName || !courseName || !email) {
      setNgcoaStatus((s) => ({ ...s, error: 'Please fill in all fields.' }));
      return;
    }
    if (!/.+@.+\..+/.test(email)) {
      setNgcoaStatus((s) => ({ ...s, error: 'Please enter a valid email.' }));
      return;
    }
    const spam = evaluateLeadSpam({
      firstName,
      lastName,
      email,
      org: courseName
    });
    if (spam.blocked) {
      setNgcoaStatus({
        submitting: false,
        success: false,
        error:
          'We could not submit your request. If you believe this is an error, please email support@getgolfmarshal.com.'
      });
      return;
    }
    setNgcoaStatus({ submitting: true, success: false, error: null });
    try {
      const apiBase = process.env.REACT_APP_MG2_API_URL || 'https://app.getgolfmarshal.com/api';
      const res = await fetch(`${apiBase}/leads/public`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName,
          lastName,
          email,
          org: courseName,
          phone: '',
          source: 'mg2land-ngcoa',
          botcheck: ''
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || 'Request failed');
      }
      setNgcoaStatus({ submitting: false, success: true, error: null });
      setNgcoaForm({ firstName: '', lastName: '', courseName: '', email: '' });
    } catch (err) {
      setNgcoaStatus({
        submitting: false,
        success: false,
        error: err.message || 'Submission failed. Please try again.'
      });
    }
  };

  // Handle phone number input changes
  const handlePhoneChange = (e) => {
    // Only allow numbers
    const value = e.target.value.replace(/\D/g, '');
    
    // Format the phone number as (XXX) XXX-XXXX
    if (value.length <= 3) {
      setPhoneNumber(value);
    } else if (value.length <= 6) {
      setPhoneNumber(`(${value.slice(0, 3)}) ${value.slice(3)}`);
    } else {
      setPhoneNumber(`(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`);
    }
  };

  // Handle sending demo text
  const handleSendDemoText = async (e) => {
    e.preventDefault();
    
    // Extract just the numbers
    const rawNumber = phoneNumber.replace(/\D/g, '');
    
    // Validate phone number (simple check for 10 digits)
    if (rawNumber.length !== 10) {
      setTextStatus({
        submitting: false,
        success: false,
        error: 'Please enter a valid 10-digit phone number'
      });
      return;
    }
    
    setTextStatus({
      submitting: true,
      success: false,
      error: null
    });

    try {
      // This would connect to your backend API endpoint
      const response = await fetch('/api/send-demo-text', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ phoneNumber: rawNumber })
      });

      if (response.ok) {
        setTextStatus({
          submitting: false,
          success: true,
          error: null
        });
        
        // Reset form after successful submission
        setTimeout(() => {
          setPhoneNumber('');
          setTextStatus({
            submitting: false,
            success: false,
            error: null
          });
        }, 5000);
      } else {
        const data = await response.json();
        setTextStatus({
          submitting: false,
          success: false,
          error: data.message || 'Failed to send text message'
        });
      }
    } catch (error) {
      setTextStatus({
        submitting: false,
        success: false,
        error: 'Network error. Please try again.'
      });
    }
  };

  return (
    <>
      {/* Floating CTA */}
      {showFloatingCta && (
        <div className="floating-cta show" role="region" aria-label="Sticky call to action">
          <a href={`https://app.getgolfmarshal.com/signuppro?interval=${isLegacyPricing ? 'legacy' : (isAnnualBilling ? 'yearly' : 'monthly')}`} className="btn btn-primary hover-glow shadow-lg">
            {isLegacyPricing ? 'Lock In Legacy Pricing' : 'Start Free Trial'}
          </a>
        </div>
      )}
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-50 to-primary-100">
        <div className="hero-animated-bg" aria-hidden="true" ref={parallaxRef3}></div>
        <div className="container py-10 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6" data-reveal>
                Save Hours In The Clubhouse
              </h1>
              <p className="text-xl text-gray-700 mb-8" data-reveal>
                Golf Marshal saves club operators time, reduces reporting errors, and delivers an outstanding player experience
              </p>
              <div className="flex gap-4 mb-8 flex-wrap" data-reveal>
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-secondary-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="font-medium">No more paper</span>
                </div>
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-secondary-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="font-medium">No more spreadsheets</span>
                </div>
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-secondary-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="font-medium">Increase Event ROI</span>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4" data-reveal>
                <a href={`https://app.getgolfmarshal.com/signuppro?interval=${isLegacyPricing ? 'legacy' : (isAnnualBilling ? 'yearly' : 'monthly')}`} className="btn btn-primary text-center hover-glow">
                  {isLegacyPricing ? 'Lock In Legacy Pricing' : 'Get Started'}
                </a>
                <a href="#features" className="btn btn-outline text-center hover-glow" onClick={(e) => {e.preventDefault(); scrollToSection('#features');}}>
                  Explore Features
                </a>
              </div>
            </div>
            <div className="relative" data-reveal>
              <div className="absolute -top-6 -right-6 w-40 h-40 bg-secondary-100 rounded-full opacity-50" ref={parallaxRef1}></div>
              
              {/* Screenshots carousel */}
              <div className="relative z-20 md:py-8">
                <div className="rounded-xl overflow-hidden shadow-2xl border border-gray-200 bg-white transform transition-all duration-500 hover:scale-105 pb-10 tilt-card" ref={parallaxRef2}>
                  <Slider {...screenshotSliderSettings} className="screenshot-slider">
                    <div className="px-2 py-4">
                      <img 
                        src="/images/shots/dashboard.png" 
                        alt="Golf Marshal Dashboard" 
                        className="w-full h-auto rounded-lg"
                      />
                      <div className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white text-center py-2 mt-2 rounded-b-lg">
                        <p className="font-semibold">Course Dashboard</p>
                      </div>
                    </div>
                    <div className="px-2 py-4 flex flex-col items-center justify-center">
                      <div style={{ width: "250px", margin: "0 auto" }}>
                        <img 
                          src="/images/shots/mobile.png" 
                          alt="Golf Marshal Mobile App" 
                          className="h-auto rounded-lg w-full"
                        />
                        <div className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white text-center py-2 mt-2 rounded-b-lg">
                          <p className="font-semibold">Mobile Scoring</p>
                        </div>
                      </div>
                    </div>
                    <div className="px-2 py-4">
                      <img 
                        src="/images/shots/skins.png" 
                        alt="Golf Marshal Skins Game" 
                        className="w-full h-auto rounded-lg object-contain"
                      />
                      <div className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white text-center py-2 mt-2 rounded-b-lg">
                        <p className="font-semibold">Skins Game Management</p>
                      </div>
                    </div>
                    <div className="px-2 py-4">
                      <img 
                        src="/images/shots/scorecard.png" 
                        alt="Golf Marshal Scorecard" 
                        className="w-full h-auto rounded-lg object-contain"
                      />
                      <div className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white text-center py-2 mt-2 rounded-b-lg">
                        <p className="font-semibold">Printable Scorecard</p>
                      </div>
                    </div>
                  </Slider>
                </div>
              </div>
              
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-primary-200 rounded-full opacity-40"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-12 bg-white">
        <div className="container">
          <h2 className="text-center text-gray-500 text-lg mb-8">Trusted by golf courses nationwide</h2>
          <div className="px-4">
            <Slider {...partnerSliderSettings}>
              <div className="px-4">
                <a href="https://www.stegengolf.com" target="_blank" rel="noopener noreferrer" className="transition-transform hover:scale-105 block">
                  <div className="flex justify-center items-center h-40">
                    <img 
                      src="/images/partners/stegen.jpg" 
                      alt="Ste. Genevieve Golf Club" 
                      className="h-16 md:h-24 object-contain"
                    />
                  </div>
                </a>
              </div>
              
              <div className="px-4">
                <a href="https://www.pccgolf.net" target="_blank" rel="noopener noreferrer" className="transition-transform hover:scale-105 block">
                  <div className="flex justify-center items-center h-40">
                    <img 
                      src="/images/partners/pcc2.jpg" 
                      alt="Perryville Country Club" 
                      className="h-16 md:h-24 object-contain"
                    />
                  </div>
                </a>
              </div>
              
              <div className="px-4">
                <a href="https://www.oakvalleygolfcourse.com" target="_blank" rel="noopener noreferrer" className="transition-transform hover:scale-105 block">
                  <div className="flex justify-center items-center h-40">
                    <img 
                      src="/images/partners/oakvalley.png" 
                      alt="Oak Valley Golf Course" 
                      className="h-16 md:h-24 object-contain"
                    />
                  </div>
                </a>
              </div>
              
              <div className="px-4">
                <a href="https://www.sugarcreekgolfcourse.com" target="_blank" rel="noopener noreferrer" className="transition-transform hover:scale-105 block">
                  <div className="flex justify-center items-center h-40">
                    <img 
                      src="/images/partners/sugarcreek.png" 
                      alt="Sugar Creek Golf Course" 
                      className="h-16 md:h-24 object-contain"
                    />
                  </div>
                </a>
              </div>
              
              <div className="px-4">
                <a href="https://crystalhighlandsgolf.com" target="_blank" rel="noopener noreferrer" className="transition-transform hover:scale-105 block">
                  <div className="flex justify-center items-center h-40">
                    <img 
                      src="/images/partners/crystal.jpg" 
                      alt="Crystal Highlands Golf" 
                      className="h-28 md:h-40 object-contain"
                    />
                  </div>
                </a>
              </div>
            </Slider>
          </div>
        </div>
      </section>
      
      {/* Stats Section */}
      <section className="py-16 bg-primary-700 text-white">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div className="flex flex-col items-center">
              <div className="text-5xl font-bold mb-2">3.5X</div>
              <p className="text-primary-100">Faster Event Setup</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="text-5xl font-bold mb-2">45%</div>
              <p className="text-primary-100">Increase in Mobile Scoring</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="text-5xl font-bold mb-2">10k+</div>
              <p className="text-primary-100">Events Run</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="text-5xl font-bold mb-2">4.8</div>
              <p className="text-primary-100">Customer Satisfaction</p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section id="features" className="section bg-gray-50">
        <div className="container">
          <div className="text-center max-w-3xl mx-auto mb-16" data-reveal>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Everything You Need for Seamless Golf Events</h2>
            <p className="text-xl text-gray-600">
              Streamlined management for organizers, effortless participation for players.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8" data-reveal>
            {/* Feature 1 */}
            <div id="tournament" className="card neo-card hover:shadow-lg transition-shadow">
              <div className="rounded-full bg-primary-100 p-3 w-14 h-14 flex items-center justify-center mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Tournaments</h3>
              <p className="text-gray-600 mb-4">
                Create and manage tournaments with custom filters for flights, skins, handicaps, and multi-day events.
              </p>
              <ul className="text-gray-600 space-y-2 mb-4">
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-secondary-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Customizable flight groupings
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-secondary-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Automatic scoring calculations
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-secondary-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Multi-day tournament support
                </li>
              </ul>
            </div>

            {/* Feature 2 */}
            <div id="league" className="card neo-card hover:shadow-lg transition-shadow">
              <div className="rounded-full bg-primary-100 p-3 w-14 h-14 flex items-center justify-center mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Leagues</h3>
              <p className="text-gray-600 mb-4">
                Highly customizable league setup with full handicap support and automated scheduling.
              </p>
              <ul className="text-gray-600 space-y-2 mb-4">
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-secondary-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Season-long standings
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-secondary-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Advanced handicap integration
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-secondary-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Flexible team formations
                </li>
              </ul>
            </div>

            {/* Feature 3 */}
            <div id="mobile" className="card neo-card hover:shadow-lg transition-shadow">
              <div className="rounded-full bg-primary-100 p-3 w-14 h-14 flex items-center justify-center mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Mobile Scoring</h3>
              <p className="text-gray-600 mb-4">
                Players can score directly from their phones without downloading any app.
              </p>
              <ul className="text-gray-600 space-y-2 mb-4">
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-secondary-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  No app download required
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-secondary-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Real-time leaderboard updates
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-secondary-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Works offline with auto-sync
                </li>
              </ul>
            </div>

            {/* Feature 4 */}
            <div id="communication" className="card neo-card hover:shadow-lg transition-shadow relative pb-12">
              <div className="rounded-full bg-primary-100 p-3 w-14 h-14 flex items-center justify-center mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Text Communication</h3>
              <p className="text-gray-600 mb-4">
                Keep everyone informed with integrated text messaging features
              </p>
              <ul className="text-gray-600 space-y-2 mb-4">
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-secondary-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Event Updates
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-secondary-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Tee Time Reminders
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-secondary-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Custom Message Templates
                </li>
              </ul>
              <button
                type="button"
                className="absolute bottom-0 left-0 right-0 bg-yellow-400 text-yellow-900 text-xs font-bold text-center px-3 py-2 shadow hover:bg-yellow-300 transition-colors cursor-pointer"
                onClick={navigateToTryMarshalTextAndFocusPhone}
                aria-label="Go to Try Golf Marshal Text and focus the phone number field"
              >
                Click Here To Test!
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky Walkthrough Section */}
      <section className="section bg-white">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="lg:sticky lg:top-24 self-start" data-reveal>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">How It Works</h2>
              <p className="text-gray-600">A quick look at setting up your next tournament or league.</p>
            </div>
            <div className="space-y-10">
              <div className="card glass" data-reveal>
                <h3 className="text-xl font-bold mb-2">1. Create Event</h3>
                <p className="text-gray-600">Set format, flights, and payouts with presets, then invite players.</p>
              </div>
              <div className="card glass" data-reveal>
                <h3 className="text-xl font-bold mb-2">2. Collect Signups</h3>
                <p className="text-gray-600">Players register online; handicaps and teams sync automatically.</p>
              </div>
              <div className="card glass" data-reveal>
                <h3 className="text-xl font-bold mb-2">3. Score Live</h3>
                <p className="text-gray-600">Mobile scoring updates leaderboards and skins in real time.</p>
              </div>
              <div className="card glass" data-reveal>
                <h3 className="text-xl font-bold mb-2">4. Report & Wrap</h3>
                <p className="text-gray-600">Export reports, print scorecards, and message final results.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="section bg-white">
        <div className="container">
          <div className="text-center max-w-3xl mx-auto mb-16" data-reveal>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Simple, Transparent Pricing</h2>
            <p className="text-xl text-gray-600">
              Choose the plan that works best for your events
            </p>
          </div>

          {/* Legacy Pricing Plan (Until 12/31/2025) */}
          {isLegacyPricing && (
            <div className="max-w-2xl mx-auto mb-8">
              <div className="card border-2 border-red-500 shadow-xl relative transform hover:scale-105 transition-transform duration-300 bg-gradient-to-br from-red-50 to-red-100">
                <div className="absolute top-0 right-0 bg-red-500 text-white px-6 py-2 text-sm font-bold rounded-bl-lg">
                  LEGACY PRICING
                </div>
                <div className="absolute top-0 left-0 bg-yellow-500 text-yellow-900 px-4 py-1 text-xs font-bold rounded-br-lg">
                  ENDS 12/31/2025
                </div>
                <div className="p-6 border-b border-red-200">
                  <h3 className="text-3xl font-bold mb-3 text-red-800">Pro Legacy</h3>
                  <p className="text-gray-700 mb-6">Lock in our original pricing for 2026</p>
                  
                  <div className="text-center">
                    <div className="flex items-end justify-center mb-2">
                      <span className="text-5xl font-bold text-red-800">$599</span>
                      <span className="text-gray-600 ml-3 text-xl">/year</span>
                    </div>
                    <div className="text-lg text-green-600 font-semibold mb-4">
                      Save $831.40 (58% off future pricing)
                    </div>
                    <div className="text-sm text-gray-600 bg-white p-3 rounded-lg border">
                      <strong>After 12/31/2025:</strong> $2,041.20/year (annual) or $189/month
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-secondary-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-base">Everything in Pro Plan</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-secondary-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-base">Locked-in pricing for 2026</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-secondary-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-base">No price increases in 2026</span>
                    </li>
                  </ul>
                                     <a href="https://app.getgolfmarshal.com/signuppro?interval=legacy" className="btn bg-red-600 hover:bg-red-700 text-white w-full text-lg py-4">Lock In Legacy Pricing</a>
                </div>
              </div>
            </div>
          )}

          {/* Featured Pro + Enterprise Plans (side-by-side on md+) */}
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 items-start mb-12">
            {/* Pro Plan */}
            <div className={`card border-2 ${isLegacyPricing ? 'border-gray-300' : 'border-primary-500'} shadow-xl relative transform scale-90 hover:scale-105 transition-transform duration-300 h-full flex flex-col`}>
              <div className={`absolute top-0 right-0 ${isLegacyPricing ? 'bg-gray-500' : 'bg-primary-500'} text-white px-4 py-1 text-sm font-bold rounded-bl-lg`}>
                {isLegacyPricing ? 'FUTURE PRICING' : 'MOST POPULAR'}
              </div>
              <div className="p-6 border-b border-gray-200">
                  <h3 className="text-2xl font-bold mb-2">{isLegacyPricing ? 'Pro (After 12/31/2025)' : 'Pro'}</h3>
                  <p className="text-gray-600 mb-4">{isLegacyPricing ? 'Future pricing for active golf communities' : 'For active golf communities'}</p>
                
                {/* Billing Toggle */}
                <div className="flex items-center justify-center mb-4">
                  <span className={`text-sm font-medium ${!isAnnualBilling ? 'text-primary-600' : 'text-gray-500'}`}>Monthly</span>
                  <button
                    onClick={() => setIsAnnualBilling(!isAnnualBilling)}
                    role="switch"
                    aria-checked={isAnnualBilling}
                    aria-label={isAnnualBilling ? 'Switch to monthly billing' : 'Switch to annual billing'}
                    className={`mx-3 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                      isAnnualBilling ? 'bg-primary-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        isAnnualBilling ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className={`text-sm font-medium ${isAnnualBilling ? 'text-primary-600' : 'text-gray-500'}`}>Annual</span>
                </div>
                
                {/* Dynamic Pricing Display */}
                <div className="text-center">
                  {!isAnnualBilling ? (
                    <div className="flex items-end justify-center mb-1">
                      <span className="text-4xl font-bold">$189</span>
                      <span className="text-gray-600 ml-2">/month</span>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <div className="flex items-end justify-center">
                        <span className="text-4xl font-bold">$2,041.20</span>
                        <span className="text-gray-600 ml-2">/year</span>
                      </div>
                      <div className="text-sm text-green-600 font-semibold">
                        Save $226.80 (10% off)
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-secondary-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-base">Unlimited Tournaments</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-secondary-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-base">Unlimited Leagues</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-secondary-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-base">Unlimited Players</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-secondary-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-base">Handicap Tracking</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-secondary-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-base">Import Player Rosters</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-secondary-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-base">Email Reminders</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-secondary-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-base">Scorecard & Cart Sign Printing</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-secondary-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-base">Reporting</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-secondary-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-base">Live Scoring</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-secondary-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-base">Event Host Access</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-secondary-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-base">Text Marketing</span>
                  </li>
                </ul>
                <a href={`https://app.getgolfmarshal.com/signuppro?interval=${isAnnualBilling ? 'yearly' : 'monthly'}`} className="btn btn-primary w-full mt-auto">Get Started</a>
              </div>
            </div>

            {/* Enterprise Plan */}
            <div className={`card border-2 ${isLegacyPricing ? 'border-gray-300' : 'border-primary-500'} shadow-xl relative transform scale-90 hover:scale-105 transition-transform duration-300 h-full flex flex-col`}>
              <div className="p-6 border-b border-gray-200 text-center">
                <h3 className="text-2xl font-bold mb-2">Enterprise</h3>
                <p className="text-gray-600 mb-4">For multi-course operators</p>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-secondary-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm font-medium">Everything in Pro</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-secondary-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm font-medium">Multi-Management</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-secondary-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm font-medium">Complete White Label</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-secondary-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm font-medium">API Access & Webhooks</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-secondary-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm font-medium">Negotiable SMS Rates</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-secondary-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm font-medium">Analytics & Reporting</span>
                  </div>
                </div>
                <div className="text-center mt-auto">
                  <a href="mailto:dthomas@getgolfmarshal.com" className="btn btn-outline">Contact Sales</a>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-5xl mx-auto mt-10">
            <button
              type="button"
              onClick={openNgcoaModal}
              className="w-full rounded-xl border-2 border-primary-200 bg-gradient-to-r from-primary-50 to-secondary-50 px-4 py-4 text-center text-base font-semibold text-primary-800 shadow-sm transition hover:border-primary-400 hover:shadow-md md:text-lg"
            >
              NGCOA Member? Fill out the form to receive an additional discount
            </button>
          </div>

          
        </div>
      </section>

      

      {/* Testimonials Section */}
      <section id="testimonials" className="section bg-gray-50">
        <div className="container">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">What Club Professionals Are Saying</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="card">
              <div className="mb-6">
                <div>
                  <h3 className="text-lg font-bold">Josh Huelsing</h3>
                  <p className="text-gray-600">General Manager, Crystal Highlands Golf Course</p>
                </div>
              </div>
              <p className="text-gray-600 italic">
                "We started using Golf Marshal in the Spring of 2022. We can run our tournaments more quickly, communicate weekly league standings, produce custom scorecards, and so much more. Plus, customer support is a simple phone call away with the product made in our home state of Missouri."
              </p>
            </div>

            {/* Testimonial 2 */}
            <div className="card">
              <div className="mb-6">
                <div>
                  <h3 className="text-lg font-bold">Chad Schilli</h3>
                  <p className="text-gray-600">General Manager, Ste. Genevieve Golf Club</p>
                </div>
              </div>
              <p className="text-gray-600 italic">
                "Golf Marshal has been a game changer for our golf course when it comes to tournaments and outings. We no longer have to manually do flights, skins, and scores. With a click of a button everything is sorted out. The amount of time and effort that Golf Marshal saves makes it a no-brainer for courses looking to upgrade their tournament experience."
              </p>
            </div>

            {/* Testimonial 3 */}
            <div className="card">
              <div className="mb-6">
                <div>
                  <h3 className="text-lg font-bold">Bonnie Black</h3>
                  <p className="text-gray-600">Club House Manager, Perryville Country Club</p>
                </div>
              </div>
              <p className="text-gray-600 italic">
                "We don't have to be in the clubhouse to sign people up. As long as we have an internet connection, we can log in anywhere! We don't have to spend so much time filling out scorecards and leaderboards, making cart signs, or updating league numbers.... It's all just a mouse click away! Saves so much time and energy so we can focus on other things."
              </p>
            </div>
          </div>
        </div>
      </section>

     





      {/* Try Golf Marshal Text Section */}
      <section id="try-golf-marshal-text" className="section bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-50">
        <div className="container">
          <div className="text-center max-w-3xl mx-auto mb-6">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Try Golf Marshal Text</h2>
            <p className="text-xl text-gray-600 mb-2">
              Enhance tournament, league, sponsor, and event communication through the power of text messaging.
            </p>
            <p className="text-gray-700">Text from your course landline number.</p>
          </div>
          <div className="rounded-3xl bg-white/70 backdrop-blur-sm shadow-lg p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <div className="p-4 bg-white rounded-xl shadow-sm ring-1 ring-amber-100/60">
              {/* TextMessage.Us Inline Widget */}
              <div className="textmessage-us-smart-widget" data-tenant-id="golf-marshals-text-service" data-mode="inline"></div>
              </div>
              <div className="flex justify-center md:justify-center">
              {/* iPhone-style screenshot frame */}
              <div className="relative w-[220px] sm:w-[240px] md:w-[330px] lg:w-[390px] transform rotate-6">
                <div className="relative rounded-[46px] p-[6px] bg-gradient-to-br from-neutral-700 via-neutral-800 to-neutral-900 shadow-2xl">
                  <div className="relative rounded-[40px] bg-black p-[6px]">
                    {/* Notch cluster */}
                    <div className="absolute top-2 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
                      <div className="h-1.5 w-16 bg-neutral-700 rounded-full"></div>
                      <div className="h-2.5 w-2.5 bg-neutral-600 rounded-full"></div>
                    </div>
                    {/* Screen */}
                    <div className="relative rounded-[34px] overflow-hidden bg-white px-4 py-10 md:px-6 md:py-16 lg:px-8 lg:py-20">
                      <img
                        src="/images/shots/text.png"
                        alt="Golf Marshal text messaging screenshot"
                        className="w-full h-auto rounded-xl shadow"
                      />
                    </div>
                    {/* Side buttons */}
                    <div className="absolute -left-1 top-16 h-10 w-1 bg-neutral-700 rounded"></div>
                    <div className="absolute -left-1 top-28 h-20 w-1 bg-neutral-700 rounded"></div>
                    <div className="absolute -right-1 top-24 h-16 w-1 bg-neutral-700 rounded"></div>
                  </div>
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ready to Transform Section */}
      <section id="schedule-demo" className="section bg-gray-50">
        <div className="container">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Transform Your Golf Operations?</h2>
              <p className="text-xl text-gray-600 mb-8">
                Schedule a personalized onboarding call today and we'll have you up and running in no-time!
              </p>
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-secondary-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="font-medium">30-minute session</span>
                </div>
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-secondary-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="font-medium">Live product tour</span>
                </div>
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-secondary-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="font-medium">Q&A with our team</span>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
              {/* Calendly inline widget */}
              <div className="rounded-xl overflow-hidden shadow-lg bg-white p-4">
                <div 
                  ref={calendlyRef}
                  className="calendly-inline-widget" 
                  data-url="https://calendly.com/golfmarshal/demo" 
                  style={{ width: '100%', minWidth: 0, height: '600px' }} 
                />
              </div>

              {/* Lead capture form */}
              <div className="rounded-xl shadow-lg bg-white p-6 ring-1 ring-gray-100">
                <h3 className="text-2xl font-bold mb-1">Get a Tailored Walkthrough</h3>
                <p className="text-gray-600 mb-6">Tell us a bit about your course and we’ll reach out.</p>
                <form onSubmit={handleLeadSubmit} noValidate className="relative">
                  <input
                    type="text"
                    name="botcheck"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="absolute left-[-9999px] w-px h-px overflow-hidden opacity-0 pointer-events-none"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="relative focus-within:scale-[1.01] transition-transform">
                      <input ref={firstNameRef} onKeyDown={(e) => handleLeadKeyDown(e, 'firstName')} id="firstName" name="firstName" type="text" value={leadForm.firstName} onChange={handleLeadChange} className="input peer" placeholder="First Name" required />
                      <label htmlFor="firstName" className="float-label">First Name</label>
                    </div>
                    <div className="relative focus-within:scale-[1.01] transition-transform">
                      <input ref={lastNameRef} onKeyDown={(e) => handleLeadKeyDown(e, 'lastName')} id="lastName" name="lastName" type="text" value={leadForm.lastName} onChange={handleLeadChange} className="input peer" placeholder="Last Name" required />
                      <label htmlFor="lastName" className="float-label">Last Name</label>
                    </div>
                    <div className="md:col-span-2 relative focus-within:scale-[1.01] transition-transform">
                      <input ref={emailRef} onKeyDown={(e) => handleLeadKeyDown(e, 'email')} id="email" name="email" type="email" value={leadForm.email} onChange={handleLeadChange} className="input peer" placeholder="Email" required />
                      <label htmlFor="email" className="float-label">Email</label>
                    </div>
                    <div className="md:col-span-2 relative focus-within:scale-[1.01] transition-transform">
                      <input ref={orgRef} onKeyDown={(e) => handleLeadKeyDown(e, 'org')} id="org" name="org" type="text" value={leadForm.org} onChange={handleLeadChange} className="input peer" placeholder="Course or Organization Name" required />
                      <label htmlFor="org" className="float-label">Course or Organization Name</label>
                    </div>
                    <div className="md:col-span-2 relative focus-within:scale-[1.01] transition-transform">
                      <input ref={phoneRef} onKeyDown={(e) => handleLeadKeyDown(e, 'phone')} id="phone" name="phone" type="tel" inputMode="tel" value={leadForm.phone} onChange={handleLeadPhoneChange} className="input peer" placeholder="(555) 123-4567" required />
                      <label htmlFor="phone" className="float-label">Phone</label>
                    </div>
                  </div>
                  {leadStatus.error && (
                    <div className="mt-4 text-sm text-red-600" role="alert">{leadStatus.error}</div>
                  )}
                  {leadStatus.success && (
                    <div className="mt-4 text-sm text-green-600" role="status">Thanks! We’ll be in touch shortly.</div>
                  )}
                  <button ref={submitRef} type="submit" disabled={leadStatus.submitting} className={`btn w-full mt-6 ${leadStatus.submitting ? 'cursor-wait opacity-80 btn-secondary' : 'btn-primary'}`}>
                    {leadStatus.submitting ? 'Submitting…' : 'Get Started'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lead success modal */}
      {leadModalOpen && (
        <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setLeadModalOpen(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-2xl font-bold mb-2">Thanks — we received your request!</h3>
            <p className="text-gray-600 mb-4">Our team will contact you within 24 hours to schedule your walkthrough and help you get started.</p>
            <div className="flex gap-2 justify-end">
              <a href="https://app.getgolfmarshal.com/signuppro" className="btn btn-outline">See plans</a>
              <button className="btn btn-primary" onClick={() => setLeadModalOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {ngcoaModalOpen && (
        <div role="dialog" aria-modal="true" aria-labelledby="ngcoa-modal-title" className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={closeNgcoaModal}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-4 mb-4">
              <h3 id="ngcoa-modal-title" className="text-2xl font-bold pr-2">NGCOA Member</h3>
              <button type="button" className="text-gray-500 hover:text-gray-800 shrink-0 p-1" onClick={closeNgcoaModal} aria-label="Close">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {ngcoaStatus.success ? (
              <div className="text-center py-2">
                <p className="text-green-600 font-medium mb-6" role="status">
                  Thanks! We’ll be in touch shortly.
                </p>
                <button type="button" className="btn btn-primary w-full" onClick={closeNgcoaModal}>
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleNgcoaSubmit} noValidate className="space-y-4 relative">
                <input
                  type="text"
                  name="botcheck"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute left-[-9999px] w-px h-px overflow-hidden opacity-0 pointer-events-none"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative focus-within:scale-[1.01] transition-transform">
                    <input
                      id="ngcoa-firstName"
                      name="firstName"
                      type="text"
                      value={ngcoaForm.firstName}
                      onChange={handleNgcoaChange}
                      className="input peer"
                      placeholder="First Name"
                      autoComplete="given-name"
                      required
                    />
                    <label htmlFor="ngcoa-firstName" className="float-label">First Name</label>
                  </div>
                  <div className="relative focus-within:scale-[1.01] transition-transform">
                    <input
                      id="ngcoa-lastName"
                      name="lastName"
                      type="text"
                      value={ngcoaForm.lastName}
                      onChange={handleNgcoaChange}
                      className="input peer"
                      placeholder="Last Name"
                      autoComplete="family-name"
                      required
                    />
                    <label htmlFor="ngcoa-lastName" className="float-label">Last Name</label>
                  </div>
                </div>
                <div className="relative focus-within:scale-[1.01] transition-transform">
                  <input
                    id="ngcoa-courseName"
                    name="courseName"
                    type="text"
                    value={ngcoaForm.courseName}
                    onChange={handleNgcoaChange}
                    className="input peer"
                    placeholder="Course Name"
                    autoComplete="organization"
                    required
                  />
                  <label htmlFor="ngcoa-courseName" className="float-label">Course Name</label>
                </div>
                <div className="relative focus-within:scale-[1.01] transition-transform">
                  <input
                    id="ngcoa-email"
                    name="email"
                    type="email"
                    inputMode="email"
                    value={ngcoaForm.email}
                    onChange={handleNgcoaChange}
                    className="input peer"
                    placeholder="Email"
                    autoComplete="email"
                    required
                  />
                  <label htmlFor="ngcoa-email" className="float-label">Email</label>
                </div>
                {ngcoaStatus.error && (
                  <div className="text-sm text-red-600" role="alert">{ngcoaStatus.error}</div>
                )}
                <button
                  type="submit"
                  disabled={ngcoaStatus.submitting}
                  className={`btn btn-primary w-full ${ngcoaStatus.submitting ? 'cursor-wait opacity-80' : ''}`}
                >
                  {ngcoaStatus.submitting ? 'Submitting…' : 'Access Discount'}
                </button>
              </form>
            )}
            <div className="mt-6 pt-6 border-t border-gray-200 text-center">
              <p className="text-sm text-gray-600 mb-2">Or call</p>
              <a href="tel:+13055827571" className="text-lg font-semibold text-primary-700 hover:underline">
                305.582.7571
              </a>
            </div>
          </div>
        </div>
      )}

      {/* FAQ Section */}
      <section className="section bg-white">
        <div className="container">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Frequently Asked Questions</h2>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="space-y-6">
              {/* FAQ Item 1 */}
              <div className="card">
                <h3 className="text-xl font-bold mb-3">How long does it take to set up Golf Marshal?</h3>
                <p className="text-gray-600">
                  Less than 1 hour. Our onboarding team will help you import your existing data, train your staff on the system, and show you were you can review our comprehensive knowledge base and schedule training calls.
                </p>
              </div>

              {/* FAQ Item 2 */}
              <div className="card">
                <h3 className="text-xl font-bold mb-3">Do players need to download an app to use mobile scoring?</h3>
                <p className="text-gray-600">
                  No! Golf Marshal uses a mobile-optimized web application that works on any smartphone or tablet without requiring any downloads.
                </p>
              </div>

              {/* FAQ Item 3 */}
              <div className="card">
                <h3 className="text-xl font-bold mb-3">Can I customize the system to match our tournament formats?</h3>
                <p className="text-gray-600">
                  Absolutely. Golf Marshal is designed to be highly flexible and can accommodate virtually any tournament format, scoring system, or league structure. Have a league or tournament format that you would like to see streamlined in the platform? We love that! Send us a note to support@getgolfmarshal.com and we'll take a look!
                </p>
              </div>

              {/* FAQ Item 4 */}
              <div className="card">
                <h3 className="text-xl font-bold mb-3">What kind of support do you offer?</h3>
                <p className="text-gray-600">
                  All plans include email support. Pro and Enterprise plans include phone support during business hours, while Enterprise customers also receive a dedicated account manager.
                </p>
              </div>

              {/* FAQ Item 5 */}
              <div className="card">
                <h3 className="text-xl font-bold mb-3">Is there a long-term contract?</h3>
                <p className="text-gray-600">
                  No. Golf Marshal is available on a month-to-month basis, though we do offer discounts for annual payments. You can cancel at any time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>


    </>
  );
};

export default Home; 